import { useEffect, useMemo, useRef, useState } from "react";

import { useSelector, useDispatch } from "react-redux";
import { selectUsedTheme } from "_redux/slices/timeline/theme";
import { selectFirebaseUser, selectUserDb } from '_redux/slices/main-screen/user';
import { deleteBookingNote, resetDeleteBookingNote, selectDeleteBookingNoteStatus } from "_redux/slices/timeline/timeline-bookings";

import { Colors } from "_styles";
import { Form } from "react-bootstrap";
import { CustomSpinner } from '_atoms';

import moment from "moment";

const suggestionList = [
    "Can walk up the ramp",
    "Can walk on to plane",
    "Chair to gate only",
    "Chair to top of ramp",
    "Needs aisle chair",
    "Help from gate to plane",
    "Cannot stand for long",
    "Can walk short distances",
    "Can walk up steps",
    "With family",
    "Gone through Security",
    "Airside",
];

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const normalizeBookingNotes = (bookingNotes) => {
    const normalizeNoteText = (value) => {
        if (typeof value === "string") {
            const trimmed = value.trim();
            return trimmed || null;
        }

        if (Array.isArray(value)) {
            const flattened = value
                .map((item) => {
                    if (typeof item === "string") return item.trim();
                    if (item && typeof item === "object") return normalizeNoteText(item.text);
                    return "";
                })
                .filter(Boolean);

            if (!flattened.length) return null;
            return flattened.join(". ");
        }

        if (value && typeof value === "object") {
            return normalizeNoteText(value.text);
        }

        return null;
    };

    const normalizeNoteEntry = (note) => {
        if (typeof note === "string") {
            const text = normalizeNoteText(note);
            return text ? { text } : null;
        }

        if (!note || typeof note !== "object") return null;

        const text = normalizeNoteText(note.text);
        if (!text) return null;

        return {
            ...note,
            text,
        };
    };

    if (Array.isArray(bookingNotes)) {
        return bookingNotes.map(normalizeNoteEntry).filter(Boolean);
    }

    if (typeof bookingNotes === "string" && bookingNotes.trim()) {
        return [{ text: bookingNotes.trim() }];
    }

    if (bookingNotes && typeof bookingNotes === "object") {
        const normalized = normalizeNoteEntry(bookingNotes);
        return normalized ? [normalized] : [];
    }

    return [];
};

const formatBookingNoteTimestamp = (note) => {
    const timestamp = note?.time || note?.timestamp;
    return timestamp ? moment(new Date(timestamp)).format("DD/MM/YYYY HH:mm") : "";
};

const BookingNotesSuggestions = ({ setBookingNotes, bookingNotes, booking }) => {

    const dispatch = useDispatch();


    const usedTheme = useSelector(selectUsedTheme);
    const firebaseUser = useSelector(selectFirebaseUser);
    const dbUser = useSelector(selectUserDb)
    const deleteBookingNoteStatus = useSelector(selectDeleteBookingNoteStatus);

    const [selectedSuggestions, setSelectedSuggestions] = useState([]);
    const [noteInput, setNoteInput] = useState("");
    const [clickedIndexToDelete, setClickedIndexToDelete] = useState(null);
    const draftBaseNotesRef = useRef(null);
    const draftTimeRef = useRef(null);

    const bookingNoteEntries = useMemo(() => normalizeBookingNotes(bookingNotes), [bookingNotes]);
    const selectedSuggestionTexts = useMemo(() => {
        const draftText = noteInput.trim();
        const latestNoteText = bookingNoteEntries[bookingNoteEntries.length - 1]?.text || "";
        const textToCheck = draftText || latestNoteText;

        return suggestionList.filter((suggestion) => {
            const regex = new RegExp(`\\b${escapeRegExp(suggestion)}\\b`, "i");
            return regex.test(textToCheck);
        });
    }, [bookingNoteEntries, noteInput]);

    useEffect(() => {
        if (typeof bookingNotes === "string" && bookingNotes.trim()) {
            setBookingNotes(normalizeBookingNotes(bookingNotes));
        }
    }, [bookingNotes, setBookingNotes]);

    useEffect(() => {
        dispatch(resetDeleteBookingNote());
    }, []);

    const updatePreparedNotes = (value) => {
        const trimmedNote = value.trim();

        if (!draftBaseNotesRef.current) {
            draftBaseNotesRef.current = bookingNoteEntries;
            draftTimeRef.current = new Date().toISOString();
        }

        if (!trimmedNote) {
            setBookingNotes(draftBaseNotesRef.current);
            draftBaseNotesRef.current = null;
            draftTimeRef.current = null;
            return;
        }

        const noteEntry = {
            time: draftTimeRef.current,
            text: trimmedNote,
            userId: firebaseUser?.uid || firebaseUser?.id || "unknown",
            userName: firebaseUser?.displayName || firebaseUser?.name || firebaseUser?.email || "Unknown",
        };

        setBookingNotes([...draftBaseNotesRef.current, noteEntry]);
    };

    const handleNoteInputChange = (value) => {
        setNoteInput(value);
        updatePreparedNotes(value);
    };

    const handleDeleteNote = async (noteToDelete, index) => {
        setClickedIndexToDelete(index);
        const toDelete = {
            ...noteToDelete,
            deletedAt: new Date().toISOString(),
            deletedBy: firebaseUser?.uid || firebaseUser?.id || "unknown",
            deletedByName: firebaseUser?.displayName || firebaseUser?.name || firebaseUser?.email || "Unknown",
        }
        const updatedNotes = bookingNoteEntries.filter((_, i) => i !== index);

        const res = await dispatch(deleteBookingNote({ bookingId: booking?._id, deletedBookingNotes: [toDelete], newBookingNotes: updatedNotes }));
        if (res?.meta?.requestStatus === "fulfilled") {
            setBookingNotes(updatedNotes);
            setClickedIndexToDelete(null);
        }
    };

    const handleSuggestionClick = (text) => {
        const isAlreadySelected = selectedSuggestions.includes(text);
        // check if already selected or not,
        let updatedSuggestions = [];
        let updatedNoteInput = noteInput;

        if (isAlreadySelected) {
            // Remove from selected and remove from notes
            updatedSuggestions = selectedSuggestions.filter((item) => item !== text);
            const regex = new RegExp(`\\b${escapeRegExp(text)}\\b\\.?\\s*`, "gi");
            updatedNoteInput = noteInput.replace(regex, "").trim();
        } else {
            // Add to selected and append to notes
            updatedSuggestions = [...selectedSuggestions, text];
            updatedNoteInput = `${noteInput.trim()}${noteInput.trim() ? ". " : ""}${text}`;
        }

        setSelectedSuggestions(updatedSuggestions);
        handleNoteInputChange(updatedNoteInput);
    };

    return (
        <div style={{ width: "100%" }}>
            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Booking Notes</Form.Label>

            {bookingNoteEntries.length > 0 && (
                <ul style={{ margin: "0 0 12px 0", paddingLeft: "18px", color: usedTheme.TEXT_COLOR }}>
                    {bookingNoteEntries.map((note, index) => (
                        <li
                            key={`${note?.time || note?.timestamp || "booking-note"}-${index}`}
                            style={{ marginBottom: index === bookingNoteEntries.length - 1 ? 0 : "8px" }}
                        >
                            <span style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
                                {note?.text}
                            </span>
                            <span style={{ fontSize: 12, opacity: 0.75 }}>
                                {" - "}
                                {note?.userName || note?.author || "Unknown"}
                                {formatBookingNoteTimestamp(note) ? ` - ${formatBookingNoteTimestamp(note)}` : ""}
                            </span>
                            {dbUser?.details?.authority === "JER" && booking?._id &&
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger rounded-pill ms-2"
                                    onClick={() => handleDeleteNote(note, index)}>
                                    {deleteBookingNoteStatus === "loading" && clickedIndexToDelete === index ? <CustomSpinner variant="light" size={"small"} /> : "Delete"}
                                </button>
                            }
                        </li>
                    ))}
                </ul>
            )}

            {/* Suggestions Pills */}
            <div className="mb-3 d-flex flex-wrap gap-2">
                {suggestionList.map((suggestion) => {
                    const selected = selectedSuggestionTexts.includes(suggestion);

                    return (
                        <button
                            key={suggestion}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className={`btn btn-sm rounded-pill border ${selected ? "btn-primary text-white" : "btn-outline-secondary"}`}
                            style={{
                                fontSize: "0.85rem",
                                padding: "0.375rem 0.75rem",
                            }}
                        >
                            {suggestion}
                        </button>
                    );
                })}
            </div>

            <Form.Control
                className="form-input"
                as="textarea"
                type="text"
                placeholder="Add a new note to booking (Optional)"
                onChange={(e) => handleNoteInputChange(e.target.value)}
                value={noteInput}
                style={{ height: '80px', backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
            />
        </div>
    )
}

export default BookingNotesSuggestions;
